import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Clock, Play, Square, Loader2, Timer, Send } from "lucide-react";

/**
 * Compact Clock-In / Clock-Out widget.
 *
 * - Polls `/shifts/current` on mount so a refresh keeps the running timer.
 * - Ticks a live "hh:mm:ss" once per second while the shift is open.
 * - Admins see a "Today's Shifts" table below (their team's shifts).
 * - Every clock-in / clock-out also fires a Telegram push (backend-driven).
 */
function fmtElapsed(startIso) {
  if (!startIso) return "00:00:00";
  const start = new Date(startIso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${ss}`;
}

function fmtClock(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtDurationSec(sec) {
  if (sec == null) return "—";
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

export default function ClockInWidget() {
  const { role } = useAuth();
  const isAdmin = role === "admin" || role === "acquisition_manager";
  const [shift, setShift] = useState(null);      // current open shift
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);           // forces re-render every second
  const [today, setToday] = useState([]);        // admin: all team shifts today
  const [loadingToday, setLoadingToday] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  // Load current shift + admin's today feed
  useEffect(() => {
    let alive = true;
    api.get("/shifts/current").then((r) => alive && setShift(r.data.shift)).catch(() => {});
    if (isAdmin) {
      setLoadingToday(true);
      api.get("/shifts/today")
        .then((r) => alive && setToday(r.data.shifts || []))
        .catch(() => {})
        .finally(() => alive && setLoadingToday(false));
    }
    return () => { alive = false; };
  }, [isAdmin]);

  // Live 1s tick while a shift is open
  useEffect(() => {
    if (!shift?.clock_in_at) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [shift?.clock_in_at]);

  const refreshToday = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/shifts/today");
      setToday(data.shifts || []);
    } catch { /* ignore */ }
  };

  const doClockIn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shifts/clock-in");
      setShift(data.shift);
      toast.success(data.already_open ? "Shift already running" : "Clocked in — have a great shift!");
      refreshToday();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clock-in failed");
    } finally { setBusy(false); }
  };

  const doClockOut = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shifts/clock-out");
      toast.success(`Clocked out — shift ${fmtDurationSec(data.shift.duration_seconds)}`);
      setShift(null);
      refreshToday();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clock-out failed");
    } finally { setBusy(false); }
  };

  const doSendShiftDaily = async () => {
    setSendingShiftDaily(true);
    try {
      const { data } = await api.post("/reports/daily-shifts/send-now");
      if (data.sent) {
        const t = data.totals || {};
        toast.success(
          `Shift hours sent — ${t.employees || 0} employees · total ${t.hours_str || "0h"}`,
          { duration: 4500 },
        );
      } else if (data.reason === "telegram_not_configured") {
        toast.error("Telegram not configured yet. Set it up in Settings first.");
      } else {
        toast.error("Report could not be sent");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report send failed");
    } finally { setSendingShiftDaily(false); }
  };

  const doSendWeeklyReport = async () => {
    setSendingReport(true);
    try {
      const { data } = await api.post("/shifts/weekly-report/send-now");
      if (data.sent) {
        toast.success(`Weekly report sent to Telegram — ${data.week_label}`);
      } else if (data.reason === "telegram_not_configured") {
        toast.error("Telegram not configured yet. Set it up in Settings first.");
      } else {
        toast.error("Report could not be sent");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report send failed");
    } finally { setSendingReport(false); }
  };

  const [sendingCallReport, setSendingCallReport] = useState(false);
  const [sendingShiftDaily, setSendingShiftDaily] = useState(false);
  const doSendCallReport = async () => {
    setSendingCallReport(true);
    try {
      const { data } = await api.post("/reports/daily-calls/send-now");
      if (data.sent) {
        const t = data.totals || {};
        toast.success(
          `Call report sent — ${t.dials || 0} dials · ${t.talked || 0} talked · ${t.hot || 0} 🔥`,
          { duration: 4500 },
        );
      } else if (data.reason === "telegram_not_configured") {
        toast.error("Telegram not configured yet. Set it up in Settings first.");
      } else {
        toast.error("Report could not be sent");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report send failed");
    } finally { setSendingCallReport(false); }
  };

  const clockedIn = !!shift?.clock_in_at;
  // reference tick so ESLint knows we intentionally re-render every second
  const elapsed = tick >= 0 ? fmtElapsed(shift?.clock_in_at) : "";

  return (
    <div className="space-y-4">
      <div
        className={`card-flat p-5 flex items-center justify-between gap-4 reveal ${
          clockedIn ? "border-emerald-300 bg-emerald-50/60" : ""
        }`}
        data-testid="clock-in-widget"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className={`w-11 h-11 rounded-full grid place-items-center ${
            clockedIn ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
          }`}>
            {clockedIn ? <Timer size={18} strokeWidth={2.2} /> : <Clock size={18} strokeWidth={2.2} />}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">
              {clockedIn ? "On the clock" : "Off the clock"}
            </div>
            {clockedIn ? (
              <div className="mt-1 flex items-baseline gap-3 flex-wrap">
                <div className="font-mono font-bold text-2xl text-emerald-800"
                     data-testid="clock-elapsed">
                  {elapsed}
                </div>
                <div className="text-xs text-[var(--app-text-muted)]">
                  Started {fmtClock(shift.clock_in_at)}
                </div>
              </div>
            ) : (
              <div className="mt-1 text-sm text-[var(--app-text-muted)]">
                Clock in when you start your shift — your admin gets a Telegram ping.
              </div>
            )}
          </div>
        </div>
        {clockedIn ? (
          <Button
            onClick={doClockOut}
            disabled={busy}
            data-testid="clock-out-btn"
            className="rounded-full h-10 px-5 font-medium text-white bg-rose-600 hover:bg-rose-700"
          >
            {busy
              ? <><Loader2 size={14} className="animate-spin mr-2" /> Ending…</>
              : <><Square size={14} className="mr-2" /> Clock Out</>}
          </Button>
        ) : (
          <Button
            onClick={doClockIn}
            disabled={busy}
            data-testid="clock-in-btn"
            className="rounded-full h-10 px-5 font-medium text-white bg-emerald-600 hover:bg-emerald-700"
          >
            {busy
              ? <><Loader2 size={14} className="animate-spin mr-2" /> Starting…</>
              : <><Play size={14} className="mr-2" /> Clock In</>}
          </Button>
        )}
      </div>

      {isAdmin && (
        <div className="card-flat p-5 reveal" data-testid="today-shifts-panel">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--app-text-muted)]">
                Today
              </div>
              <div className="font-display text-lg font-bold">Team shifts</div>
              <div className="text-[11px] text-[var(--app-text-muted)] mt-0.5">
                Shift summary auto-sends Fri 7pm ET · Daily call report auto-sends every day 7pm ET
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-xs text-[var(--app-text-muted)]">
                {today.length} shift{today.length === 1 ? "" : "s"} today
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={doSendCallReport}
                disabled={sendingCallReport}
                data-testid="daily-call-report-send-now-btn"
                className="h-8 rounded-full text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                title="Send today's per-rep call breakdown to your Telegram right now"
              >
                {sendingCallReport
                  ? <><Loader2 size={12} className="animate-spin mr-1.5" /> Sending…</>
                  : <>📞 Send call report now</>}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={doSendShiftDaily}
                disabled={sendingShiftDaily}
                data-testid="daily-shift-report-send-now-btn"
                className="h-8 rounded-full text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                title="Send today's per-employee hours worked to Telegram right now"
              >
                {sendingShiftDaily
                  ? <><Loader2 size={12} className="animate-spin mr-1.5" /> Sending…</>
                  : <>⏱ Send shift hours</>}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={doSendWeeklyReport}
                disabled={sendingReport}
                data-testid="weekly-report-send-now-btn"
                className="h-8 rounded-full text-xs"
                title="Send this week's shift summary to Telegram right now"
              >
                {sendingReport
                  ? <><Loader2 size={12} className="animate-spin mr-1.5" /> Sending…</>
                  : <><Send size={12} className="mr-1.5" /> Send weekly now</>}
              </Button>
            </div>
          </div>
          {loadingToday ? (
            <div className="py-4 text-sm text-[var(--app-text-muted)]">Loading…</div>
          ) : today.length === 0 ? (
            <div className="py-4 text-sm text-[var(--app-text-muted)]">
              No teammates have clocked in yet today.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="today-shifts-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] border-b border-[var(--app-line)]">
                    <th className="text-left py-2 pr-2">Teammate</th>
                    <th className="text-left py-2 pr-2">In</th>
                    <th className="text-left py-2 pr-2">Out</th>
                    <th className="text-left py-2 pr-2">Duration</th>
                    <th className="text-left py-2 pr-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {today.map((s) => {
                    const open = !s.clock_out_at;
                    return (
                      <tr key={s.id} className="border-b border-[var(--app-line)]/40 last:border-0"
                          data-testid={`shift-row-${s.id}`}>
                        <td className="py-2 pr-2 font-medium">
                          {s.user_name || s.user_email}
                        </td>
                        <td className="py-2 pr-2 font-mono text-xs">{fmtClock(s.clock_in_at)}</td>
                        <td className="py-2 pr-2 font-mono text-xs">{fmtClock(s.clock_out_at)}</td>
                        <td className="py-2 pr-2 font-mono text-xs">
                          {open ? "—" : fmtDurationSec(s.duration_seconds)}
                        </td>
                        <td className="py-2 pr-2">
                          {open ? (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              On shift
                            </span>
                          ) : (
                            <span className="text-xs text-[var(--app-text-muted)]">Ended</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
